/**
 * Reference data (users, cities, components, assets) loaded once per session.
 *
 * Every view needs these to render chips and populate filter menus, and they
 * change rarely, so they are fetched once after login and shared by context.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../api.js'

const TaxonomyContext = createContext(null)

/** Provider that fetches reference data once the user is authenticated. */
export function TaxonomyProvider({ children }) {
  const [data, setData] = useState({ users: [], cities: [], components: [], assets: [] })
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.users(), api.cities(), api.components(), api.assets()])
      .then(([users, cities, components, assets]) => {
        if (!cancelled) setData({ users, cities, components, assets })
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(
    () => ({
      ...data,
      error,
      usersById: new Map(data.users.map((u) => [u.id, u])),
      citiesBySlug: new Map(data.cities.map((c) => [c.slug, c])),
      componentsBySlug: new Map(data.components.map((c) => [c.slug, c])),
      assetsById: new Map(data.assets.map((a) => [a.id, a])),
    }),
    [data, error],
  )

  return <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>
}

/** Access the shared reference data. Returns empty collections before load. */
export function useTaxonomy() {
  const value = useContext(TaxonomyContext)
  if (!value) {
    throw new Error('useTaxonomy must be used inside <TaxonomyProvider>')
  }
  return value
}
